import { captureException } from '@sentry/react-router'
import { type TFunction } from 'i18next'
import { useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { getErrorMessage } from '#app/utils/misc'
import { clientLogger } from '#app/utils/pz/logger/clientLogger'
import { useOptionalUser, userHasRole } from '#app/utils/user'

const log = clientLogger.scoped('GeneralErrorBoundary')

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

/**
 * Formats error message based on user role and preference
 * - Admins can toggle between technical and friendly messages
 * - Regular users always see friendly messages
 */
function formatErrorMessage(
	message: string,
	showTechnical: boolean,
	t: TFunction,
): string {
	if (showTechnical) {
		// Show full technical details
		return message.replace(/\\n/g, '\n')
	}

	// If the server returned an i18n key, translate it for a friendly message
	if (/^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/.test(message)) {
		const translated = t(message as any)
		if (translated && translated !== message) {
			return translated
		}
	}

	// Provide friendly messages using translations
	if (message.includes('prisma') || message.includes('Prisma')) {
		return t('common.error.boundary.databaseError')
	}

	if (message.includes('INTERNAL_SERVER_ERROR')) {
		return t('common.error.boundary.serverError')
	}

	if (message.includes('validation') || message.includes('invalid')) {
		return t('common.error.boundary.validationError')
	}
	if (message.includes('not found') || message.includes('Not Found')) {
		return t('common.notFound.message')
	}
	if (
		message.includes('permission denied') ||
		message.includes('Permission Denied')
	) {
		return t('common.permissionDenied.message')
	}
	// Default friendly message
	return t('common.error.boundary.defaultError')
}

/**
 * Extracts error message from various error formats
 */
function extractErrorMessage(error: ErrorResponse): string {
	const { statusText, data } = error
	let message: string

	if (typeof data === 'string') {
		message = data
	} else if (data && typeof (data as any).error?.message === 'string') {
		message = (data as any).error.message
	} else if (data && typeof (data as any).message === 'string') {
		message = (data as any).message
	} else if (data && typeof (data as any).error === 'string') {
		message = (data as any).error
	} else {
		try {
			message = JSON.stringify(data)
		} catch {
			message = statusText
		}
	}

	return message
}

export function GeneralErrorBoundary({
	defaultStatusHandler,
	statusHandlers,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const { t } = useTranslation()
	const error = useRouteError() as unknown
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)
	const user = useOptionalUser()
	const isAdmin = userHasRole(user, 'admin')
	const [showTechnicalDetails, setShowTechnicalDetails] = useState(false)

	if (typeof document !== 'undefined') {
		log.error('Route error boundary triggered', {
			error: error instanceof Error ? error.message : String(error),
		})
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	// Extract error message
	let errorMessage: string
	let errorStatus: number | undefined

	if (isRouteErrorResponse(error)) {
		const routeError = error as ErrorResponse
		errorStatus = routeError.status

		// Use custom status handler if provided
		if (statusHandlers?.[routeError.status]) {
			return (
				<div className="mx-auto w-full max-w-[40rem] px-5 py-16 md:px-8 md:py-24">
					<div className="w-full">
						{statusHandlers?.[routeError.status]?.({
							error: routeError,
							params,
						})}
					</div>
				</div>
			)
		}

		// Use default handler
		if (defaultStatusHandler) {
			return (
				<div className="mx-auto w-full max-w-[40rem] px-5 py-16 md:px-8 md:py-24">
					<div className="w-full">
						{defaultStatusHandler({ error: routeError, params })}
					</div>
				</div>
			)
		}

		errorMessage = extractErrorMessage(routeError)
	} else {
		errorMessage = getErrorMessage(error)
	}

	// Format message based on admin status and toggle. A 403 or 404 says what
	// happened instead of the generic message.
	const showTechnical = isAdmin && showTechnicalDetails
	const formattedMessage =
		!showTechnical && errorStatus === 403
			? t('common.permissionDenied.message')
			: !showTechnical && errorStatus === 404
				? t('common.notFound.message')
				: formatErrorMessage(errorMessage, showTechnical, t)

	return (
		<div className="mx-auto w-full max-w-[40rem] px-5 py-16 md:px-8 md:py-24">
			<div className="w-full">
				<div className="flex items-center justify-between">
					<p className="text-label text-destructive">
						{errorStatus || 'Unexpected'} error
					</p>
					{isAdmin && (
						<button
							onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
							className="border-border hover:bg-muted rounded border px-3 py-1 text-xs transition-colors"
							type="button"
						>
							{showTechnicalDetails
								? t('common.error.boundary.showFriendly')
								: t('common.error.boundary.showTechnical')}
						</button>
					)}
				</div>
				<h1 className="text-title-3 text-foreground mt-3 whitespace-pre-wrap">
					{formattedMessage}
				</h1>
			</div>
		</div>
	)
}
